// Converts a raw Firestore contact (old or new shape) into a consistent
// in-memory shape with arrays for phones/emails. Always returns the rich
// fields (even if empty) so consumers don't have to null-check everywhere.
//
// Old shape (still in Firestore for un-touched records):
//   { name, phone, email }
//
// New shape (written by the new edit drawer):
//   { firstName, lastName, phones: [{value,type,primary}], emails: [...], ... }

import { cleanEmail } from "./cleanEmail";

export function normalizeContact(raw) {
  if (!raw) return null;

  // Split legacy single `name` into first/last on a best-effort basis.
  const splitName = (full) => {
    const trimmed = (full || "").trim();
    if (!trimmed) return { firstName: "", lastName: "" };
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" "),
    };
  };

  // Phones: prefer array, fall back to legacy string field.
  let phones = [];
  if (Array.isArray(raw.phones) && raw.phones.length > 0) {
    phones = raw.phones.map((p, i) => ({
      value: p.value || "",
      type: p.type || "mobile",
      primary: typeof p.primary === "boolean" ? p.primary : i === 0,
    }));
  } else if (raw.phone) {
    phones = [{ value: raw.phone, type: "mobile", primary: true }];
  }

  // Emails: same pattern. cleanEmail strips invisible pasted characters
  // (zero-width spaces etc.) that silently bounce mail — applied on READ so
  // contacts saved before the cleaning existed are healed too.
  let emails = [];
  if (Array.isArray(raw.emails) && raw.emails.length > 0) {
    emails = raw.emails.map((e, i) => ({
      value: cleanEmail(e.value),
      type: e.type || "personal",
      primary: typeof e.primary === "boolean" ? e.primary : i === 0,
    }));
  } else if (raw.email) {
    emails = [{ value: cleanEmail(raw.email), type: "personal", primary: true }];
  }

  const firstName =
    raw.firstName != null ? raw.firstName : splitName(raw.name).firstName;
  const lastName =
    raw.lastName != null ? raw.lastName : splitName(raw.name).lastName;

  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  const primaryPhone =
    phones.find((p) => p.primary)?.value || phones[0]?.value || "";
  const primaryEmail =
    emails.find((e) => e.primary)?.value || emails[0]?.value || "";

  return {
    ...raw,
    id: raw.id,
    googleResourceName: raw.googleResourceName || "",
    source: raw.source || "manual",
    createdBy: raw.createdBy || "",
    createdAt: raw.createdAt || 0,
    lastSyncedAt: raw.lastSyncedAt || 0,

    firstName,
    lastName,
    nickname: raw.nickname || "",
    name: fullName || raw.name || "",

    phones,
    emails,
    // Legacy single-string fields kept in sync with the primary entry so
    // older consumers (deal customers list, etc.) don't break.
    phone: primaryPhone,
    email: primaryEmail,

    address: {
      street: raw.address?.street || "",
      city: raw.address?.city || "",
      state: raw.address?.state || "",
      postalCode: raw.address?.postalCode || "",
      country: raw.address?.country || "",
    },

    organization: {
      company: raw.organization?.company || "",
      title: raw.organization?.title || "",
    },

    birthday: raw.birthday || "",
    homeAnniversary: raw.homeAnniversary || "",
    spouse: raw.spouse || "",

    notes: raw.notes || "",
    photoUrl: raw.photoUrl || "",

    // Labels / Groups (synced from Google contactGroups). Each entry is
    // { groupResourceName, name }. Empty array until backend syncs.
    labels: Array.isArray(raw.labels) ? raw.labels : [],
  };
}

// Inverse: takes the in-memory shape and returns the Firestore-ready payload.
// Strips legacy single-string fields and keeps the array shape canonical.
export function denormalizeContact(contact) {
  if (!contact) return {};
  const primaryPhone =
    contact.phones?.find((p) => p.primary)?.value ||
    contact.phones?.[0]?.value ||
    "";
  const primaryEmail =
    contact.emails?.find((e) => e.primary)?.value ||
    contact.emails?.[0]?.value ||
    "";

  return {
    firstName: contact.firstName || "",
    lastName: contact.lastName || "",
    nickname: contact.nickname || "",
    // Computed full name kept for legacy consumers (table search, etc.).
    name: [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim(),

    phones: (contact.phones || []).filter((p) => p.value?.trim()),
    emails: (contact.emails || []).filter((e) => e.value?.trim()),
    // Legacy single-string mirrors of the primary entry.
    phone: primaryPhone,
    email: primaryEmail,

    address: contact.address || {},
    organization: contact.organization || {},
    birthday: contact.birthday || "",
    homeAnniversary: contact.homeAnniversary || "",
    spouse: contact.spouse || "",
    notes: contact.notes || "",
    photoUrl: contact.photoUrl || "",
    labels: Array.isArray(contact.labels) ? contact.labels : [],
  };
}
